import SharedUploadDialog from './SharedUploadDialog'
import DocumentDistributionDrawer from './DocumentDistributionDrawer'
import { audienceFor, matchesLocation, audienceLabel } from './documentAudience.mjs'
// web/src/features/communities/CommunityDocsDev.jsx
import React, { useEffect, useMemo, useState } from 'react'

// Native community docs (Firestore)
import {
  listDocsFS,
  addDocFS,
  softDeleteDocFS,
  restoreDocFS,
} from '@/services/firestore/docsService'
import { createArchiveRequestTaskFS } from '@/services/firestore/taskService'

// Communities & Doc Types
import {
  subscribeCommunitiesFS,
} from '@/services/firestore/communityService'
import { listDocTypesFS } from '@/services/firestore/docTypesService'
import { getMyProfileFS } from '@/services/firestore/usersService'
import { subscribeSysConfigFS } from '@/services/firestore/sysConfigService'

// CAF mirror (read-only)
import { getApp } from 'firebase/app'
import {
  getFirestore,
  collection,
  doc,
  getDocs,
  onSnapshot,
  orderBy,
  where,
  query,
  limit as fsLimit,
} from 'firebase/firestore'

// Storage for uploads
import { storage } from '@/firebase'
import { ref as sRef, uploadBytes, getDownloadURL } from 'firebase/storage'

// Zip exporter (expects prop `items`)
import ExportDocsZip from './ExportDocsZip.jsx'

// House modal (replaces window.alert)
import ConfirmDialog from '@/components/ConfirmDialog.jsx'
import useSession from '@/hooks/useSession'

/* ---------------- helpers ---------------- */
const db = (() => {
  try {
    return getFirestore(getApp())
  } catch {
    return getFirestore()
  }
})()

function fmtTS(ts) {
  if (!ts) return '—'
  if (ts?.seconds) return new Date(ts.seconds * 1000).toLocaleString()
  const d = new Date(ts)
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString()
}
function normalizeStr(s) {
  return (s || '').toString().trim().toLowerCase()
}
function hasDownload(r) {
  return !!(r?.file?.path || r?.file?.downloadURL)
}

function ActionIcon({ kind = 'archive' }) {
  if (kind === 'distribution') {
    return <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="3" width="6" height="5" rx="1"/><path d="M12 8v5M5 16v-3h14v3"/><rect x="2" y="16" width="6" height="5" rx="1"/><rect x="16" y="16" width="6" height="5" rx="1"/></svg>
  }
  if (kind === 'restore') {
    return (
      <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
        <path
          d="M3 12a9 9 0 1 0 3-6.708M3 3v6h6"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    )
  }

  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <path
        d="M4 7.5h16M9 11v5m6-5v5M6.5 7.5l1 10a2 2 0 0 0 1.99 1.8h5.02a2 2 0 0 0 1.99-1.8l1-10M9 5h6l1 2.5H8L9 5Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

async function uploadToStorage(folder, file) {
  const safeFolder = (folder || 'docs').replace(/[^\w.\-/]/g, '_')
  const safeName = `${Date.now()}_${(file?.name || 'upload').replace(/\s+/g, '_')}`
  const fullPath = `communityDocs/${safeFolder}/${safeName}`
  const ref = sRef(storage, fullPath)
  await uploadBytes(ref, file, { contentType: file?.type || 'application/octet-stream' })
  const downloadURL = await getDownloadURL(ref)
  return {
    name: file?.name || 'upload',
    size: file?.size || 0,
    type: file?.type || '',
    downloadURL,
    path: fullPath,
  }
}

/* CAF helpers */
function normalizeCAFFileRef(f) {
  const url =
    f?.file?.downloadURL ||
    f?.downloadURL ||
    f?.url ||
    f?.fileURL || // some proposal shapes
    (typeof f === 'string' ? f : '')
  if (!url) return null

  const name =
    f?.file?.name ||
    f?.name ||
    f?.title ||
    f?.proposalName || // proposal-specific
    'CAF Upload'

  const path = f?.file?.path || f?.path || ''

  return {
    name,
    size: f?.file?.size || 0,
    type: f?.file?.type || '',
    downloadURL: url,
    path,
  }
}

// Flatten CAF *files* + *proposals* into rows for this grid
function flattenCAFFiles(cafDoc, communityName){
  const cafStatus = normalizeStr(cafDoc?.status)
  const proposalVisibleStatuses = new Set(['approved', 'complete', 'closed'])

  // proposals can live as an array field on the CAF doc under `proposals` (or legacy `proposalDocs`)
  const proposalArray = Array.isArray(cafDoc?.proposals)
    ? cafDoc.proposals
    : Array.isArray(cafDoc?.proposalDocs)
    ? cafDoc.proposalDocs
    : []

  // IMPORTANT: go back to original behavior for regular files:
  // use the first non-empty array so we don't multiply the same item.
  const baseFiles =
    (Array.isArray(cafDoc?.files) && cafDoc.files) ||
    (Array.isArray(cafDoc?.documents) && cafDoc.documents) ||
    (Array.isArray(cafDoc?.docs) && cafDoc.docs) ||
    (Array.isArray(cafDoc?.attachments) && cafDoc.attachments) ||
    []

  // proposals get appended and marked as such
  const files = [
    ...baseFiles,
    ...proposalArray.map(p => ({ ...p, __cafSource: 'cafProposal' })),
  ]

  // dedupe just in case (by downloadURL + title + origin)
  const seen = new Set()
  const rows = []

  files.forEach((f, i) => {
    const origin =
      f?.origin ||
      f?.source ||
      (f.__cafSource === 'cafProposal' ? 'cafProposal' : 'cafFile')

    if (
      origin === 'cafProposal' &&
      !proposalVisibleStatuses.has(cafStatus)
    ) {
      return
    }

    const meta = normalizeCAFFileRef(f)
    if (!meta) return

    const title =
      (f?.title && String(f.title).trim()) ||
      f?.displayName ||
      f?.name ||
      meta.name ||
      'CAF Upload'

    const docType =
      f?.docType ||
      f?.type ||
      f?.category ||
      (origin === 'cafProposal' ? 'Proposal' : '')

    const createdAt =
      f?.ts ||
      f?.uploadedAt ||
      f?.createdAt ||
      cafDoc?.updatedAt ||
      cafDoc?.createdAt ||
      null

    const key = `${meta.downloadURL || ''}::${title}::${origin}`
    if (seen.has(key)) return
    seen.add(key)

    rows.push({
      id: `${cafDoc.id}::${i}`,
      title,
      type: docType || (origin === 'cafProposal' ? 'Proposal' : 'CAF Document'),
      source: 'CAF',
      origin,
      cafNumber: cafDoc?.number || '',
      communityCode: cafDoc?.communityCode || cafDoc?.community || '',
      communityName: communityName || cafDoc?.communityName || cafDoc?.communityCode || '',
      file: meta,
      createdAt,
      archivedAt: null,
      tags: Array.isArray(f?.tags) ? f.tags : [],
      notes: typeof f?.notes === 'string' ? f.notes : '',
    })
  })

  return rows
}

// How we show the "Type" column in the grid
function displayType(row) {
  // Non-CAF docs: just the stored type
  if (row.source !== 'CAF') {
    return row.type || '—'
  }

  // CAF-derived docs:
  // If we know this came from a CAF proposal, call it "Proposal"
  if (row.origin === 'cafProposal' || row.type === 'Proposal') {
    return 'Proposal'
  }

  // otherwise use the explicit type if it isn't literally "CAF"
  if (row.type && row.type !== 'CAF') {
    return row.type
  }

  // Legacy / unknown CAF docs
  return 'CAF'
}

/* ---------------- component ---------------- */
export default function CommunityDocsDev() {
  // reference
  const [communities, setCommunities] = useState([])
  const [docTypes, setDocTypes] = useState([])
  const [liveCommunities, setLiveCommunities] = useState([])

  // data
  const [docs, setDocs] = useState([]) // native community docs
  const [cafRows, setCafRows] = useState([]) // mirrored CAF uploads
  const [busy, setBusy] = useState(false)

  // filters / toggles
  const [q, setQ] = useState('')
  const [typeFilter, setTypeFilter] = useState('All') // 'All' | 'CAF' | <typeName>
  const [communityFilter, setCommunityFilter] = useState('All')
  const [showArchived, setShowArchived] = useState(false)
  const [includeCAF, setIncludeCAF] = useState(true)

  // upload row
  const [uTitle, setUTitle] = useState('')
  const [uType, setUType] = useState('—')
  const [uCommunity, setUCommunity] = useState('')
  const [uAudience, setUAudience] = useState('community')
  const [audienceModal, setAudienceModal] = useState(false)
  const [distributionDoc, setDistributionDoc] = useState(null)
  const [distributionNotice, setDistributionNotice] = useState('')
  const [audienceBefore, setAudienceBefore] = useState({scope:'community',locations:[]})
  const [uLocations, setULocations] = useState([])
  const [uFile, setUFile] = useState(null)
  const [uTags, setUTags] = useState('') // comma-separated input
  const [uNotes, setUNotes] = useState('')
  const [uploading, setUploading] = useState(false)

  // selection (export)
  const [selected, setSelected] = useState(() => new Set())

  // dialog (house modal)
  const [dlg, setDlg] = useState({
    open: false,
    title: '',
    message: '',
    confirmLabel: 'OK',
    cancelLabel: 'Cancel',
    tone: 'default',
    hideCancel: true,
    onConfirm: null,
    onCancel: null,
  })
  const closeDlg = () => setDlg((d) => ({ ...d, open: false }))
  const showError = (title, message) =>
    setDlg({
      open: true,
      title,
      message,
      confirmLabel: 'OK',
      cancelLabel: 'Cancel',
      tone: 'default',
      hideCancel: true,
      onConfirm: closeDlg,
      onCancel: closeDlg,
    })

  // session + membership scoping
  const session = useSession() || {}
  const hasAllCommunities = session?.allCommunities === true
  const isAdminOrHigher = ['admin', 'sysconfig'].includes(
    normalizeStr(session?.adminLevel)
  )
  const [adminOnlyArchive, setAdminOnlyArchive] = useState(false)
  const sessionCommunities = useMemo(() => {
    if (hasAllCommunities) return new Set()
    const raw = liveCommunities.length
      ? liveCommunities
      : (Array.isArray(session?.communities) ? session.communities : [])
    return new Set(raw.map(normalizeStr)) // display names (e.g., "Avenir")
  }, [hasAllCommunities, liveCommunities, session])

  useEffect(() => {
    return subscribeSysConfigFS((next) => {
      setAdminOnlyArchive(next?.restrictions?.adminOnlyArchive === true)
    })
  }, [])

  useEffect(() => {
    let unsubscribe = null
    let alive = true
    ;(async () => {
      try {
        const profile = await getMyProfileFS()
        if (!alive || !profile?.email) return
        setLiveCommunities(
          Array.isArray(profile.communities) ? profile.communities : [],
        )
        unsubscribe = onSnapshot(
          doc(db, 'users', normalizeStr(profile.email)),
          (snap) => {
            if (!alive || !snap.exists()) return
            const data = snap.data() || {}
            setLiveCommunities(
              Array.isArray(data.communities) ? data.communities : [],
            )
          },
        )
      } catch (e) {
        console.warn('[CommunityDocs] live profile subscribe failed:', e)
      }
    })()
    return () => {
      alive = false
      if (unsubscribe) unsubscribe()
    }
  }, [])

  function openRestrictedArchiveDialog(row) {
    setDlg({
      open: true,
      title: 'Archive Restricted',
      message: (
        <div>
          <p style={{ marginTop: 0 }}>
            Archiving is restricted to Administrators. If you need this item deleted or archived,
            please contact an administrator.
          </p>
          <p style={{ marginBottom: 0 }}>
            If you continue, MaintainIQ will send an admin task with the details of your request.
          </p>
        </div>
      ),
      confirmLabel: 'Notify Admin',
      cancelLabel: 'Cancel',
      tone: 'default',
      hideCancel: false,
      onCancel: closeDlg,
      onConfirm: async () => {
        try {
          await createArchiveRequestTaskFS({
            entityType: 'community document',
            entityTitle: row?.title || row?.file?.name || 'Untitled document',
            communityCode: codeOf(row),
            communityName: nameForCode(codeOf(row)),
            relatedUrl: '/docs',
            relatedLabel: row?.title || 'Community Documents',
          })
          setDlg({
            open: true,
            title: 'Request Sent',
            message: 'An administrator task was created with your archive request.',
            confirmLabel: 'OK',
            cancelLabel: 'Cancel',
            tone: 'default',
            hideCancel: true,
            onConfirm: closeDlg,
            onCancel: closeDlg,
          })
        } catch (err) {
          showError(
            'Request failed',
            err?.message || 'Unable to notify the administrator right now.'
          )
        }
      },
    })
  }

  // communities should live-update so newly added properties show up immediately
  useEffect(() => {
    const off = subscribeCommunitiesFS(
      (rows) => setCommunities(Array.isArray(rows) ? rows : []),
      { onlyActive: null }
    )
    return () => off?.()
  }, [])

  // reference loads
  useEffect(() => {
    ;(async () => {
      try {
        const types = await listDocTypesFS({ onlyActive: true })
        setDocTypes(Array.isArray(types) ? types : [])
      } catch (e) {
        console.error('[CommunityDocs] init refs failed:', e)
        setDocTypes([])
      }
    })()
  }, [])

  // load native docs; re-fetch when showArchived toggles so archived are actually included
  useEffect(() => {
    ;(async () => {
      setBusy(true)
      try {
        const rows = await listDocsFS(null, !!showArchived)
        setDocs(Array.isArray(rows) ? rows : [])
      } catch (e) {
        console.error('[CommunityDocs] listDocsFS failed:', e)
        setDocs([])
      } finally {
        setBusy(false)
      }
    })()
  }, [showArchived])

  // load CAF mirror (one-shot initially + whenever includeCAF/communityFilter changes)
  useEffect(() => {
    ;(async () => {
      if (!includeCAF) {
        setCafRows([])
        return
      }
      try {
        const base = collection(db, 'caf')
        let qy
        if (communityFilter && communityFilter !== 'All') {
          qy = query(
            base,
            where('communityCode', '==', communityFilter),
            orderBy('createdAt', 'desc'),
            fsLimit(200)
          )
        } else {
          qy = query(base, orderBy('createdAt', 'desc'), fsLimit(200))
        }
        const snap = await getDocs(qy)
        const cafs = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }))

        const codeToName = new Map(
          (communities || []).map((c) => [
            c.code || c.communityCode,
            c.communityName || c.name || c.code,
          ])
        )
        const flat = cafs.flatMap((c) =>
          flattenCAFFiles(c, codeToName.get(c.communityCode) || c.communityCode)
        )
        setCafRows(flat)
      } catch (e) {
        console.warn('[CommunityDocs] CAF mirror load failed:', e)
        setCafRows([])
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeCAF, communityFilter, communities])

  /* community scoping by membership (dropdown + upload community list) */
  const scopedCommunities = useMemo(() => {
    if (!sessionCommunities.size) return communities
    return communities.filter((c) => {
      const code = normalizeStr(c.code || c.communityCode)
      const name = normalizeStr(c.communityName || c.name || c.code)
      return sessionCommunities.has(name) || sessionCommunities.has(code)
    })
  }, [communities, sessionCommunities])

  // auto-select community if exactly one permitted
  useEffect(() => {
    if (scopedCommunities.length === 1) {
      const only = scopedCommunities[0]
      const code = only.code || only.communityCode
      if (communityFilter === 'All') setCommunityFilter(code)
      if (!uCommunity) setUCommunity(code)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopedCommunities.length])

  /* name & options */
  const nameForCode = useMemo(() => {
    const m = new Map(
      communities.map((c) => [
        c.code || c.communityCode,
        c.communityName || c.name || c.code,
      ])
    )
    return (code) => m.get(code) || code || '—'
  }, [communities])

  // const communityOptions = useMemo(() => {
  //   const base = scopedCommunities.map((c) => ({
  //     code: c.code || c.communityCode,
  //     name: c.communityName || c.name || c.code,
  //   }))
  //   return base.length > 1 ? [{ code: 'All', name: 'All' }, ...base] : base
  // }, [scopedCommunities])


// Search card should see ALL communities, not scoped
// const communityOptions = useMemo(() => {
//   const base = communities.map(c => ({
//     code: c.code || c.communityCode,
//     name: c.communityName || c.name || c.code,
//   }))
//   return base.length > 1 ? [{ code: 'All', name: 'All' }, ...base] : base
// }, [communities])

// “All” + member communities
const communityOptions = useMemo(() => {
  const base = scopedCommunities
    .map(c => ({
      code: c.code || c.communityCode,
      name: c.communityName || c.name || c.code,
    }))
    .sort((a, b) =>
      (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase())
    )

  // Always prepend All
  return [{ code: 'All', name: 'All Communities' }, ...base]
}, [scopedCommunities])


  const docTypeOptions = useMemo(
    () => ['All', 'CAF', ...docTypes.map((t) => t.name)],
    [docTypes]
  )

  // helper to read code (either communityCode or community)
  const codeOf = (d) => d.communityCode || d.community || ''

  /* filtered rows (merge native + CAF) */
  // const rows = useMemo(() => {
  //   const s = normalizeStr(q)
  //   const all = [
  //     ...(Array.isArray(docs)
  //       ? docs.map((d) => ({ ...d, source: 'Community' }))
  //       : []),
  //     ...(includeCAF ? cafRows || [] : []),
  //   ]

  //   // apply membership scope on list (defensive)
  //   const scoped = sessionCommunities.size
  //     ? all.filter((d) => {
  //         const code = normalizeStr(codeOf(d))
  //         const name = normalizeStr(nameForCode(codeOf(d)))
  //         return sessionCommunities.has(code) || sessionCommunities.has(name)
  //       })
  //     : all

  //   return scoped.filter((d) => {
  //     const matchQ =
  //       !s ||
  //       normalizeStr(d.title).includes(s) ||
  //       (Array.isArray(d.tags) &&
  //         normalizeStr(d.tags.join(' ')).includes(s)) ||
  //       normalizeStr(codeOf(d)).includes(s) ||
  //       normalizeStr(nameForCode(codeOf(d))).includes(s)

  //     const typeOk =
  //       typeFilter === 'All' ||
  //       (typeFilter === 'CAF' && d.source === 'CAF') ||
  //       normalizeStr(d.type) === normalizeStr(typeFilter)

  //     const commOk =
  //       communityFilter === 'All' || codeOf(d) === communityFilter

  //     const archOk = showArchived || !d.archivedAt

  //     return matchQ && typeOk && commOk && archOk
  //   })
  // }, [
  //   docs,
  //   cafRows,
  //   q,
  //   typeFilter,
  //   communityFilter,
  //   includeCAF,
  //   showArchived,
  //   nameForCode,
  //   sessionCommunities,
  // ])

  /* unfiltered rows (merge native + CAF) */
// const rows = useMemo(() => {
//   const s = normalizeStr(q)
//   const all = [
//     ...(Array.isArray(docs) ? docs.map(d => ({ ...d, source: 'Community' })) : []),
//     ...(includeCAF ? (cafRows || []) : [])
//   ]

//   // apply membership scope on list:
//   // - Community docs: restricted to sessionCommunities
//   // - CAF docs: ALWAYS visible (research use-case)
//   const scoped = sessionCommunities.size
//     ? all.filter(d => {
//         if (d.source === 'CAF') {
//           // business rule: any CAF-associated document is visible to this user
//           return true
//         }
//         const code = normalizeStr(codeOf(d))
//         const name = normalizeStr(nameForCode(codeOf(d)))
//         return sessionCommunities.has(code) || sessionCommunities.has(name)
//       })
//     : all

//   return scoped.filter(d => {
//     const matchQ =
//       !s ||
//       normalizeStr(d.title).includes(s) ||
//       (Array.isArray(d.tags) && normalizeStr(d.tags.join(' ')).includes(s)) ||
//       normalizeStr(codeOf(d)).includes(s) ||
//       normalizeStr(nameForCode(codeOf(d))).includes(s)

//     const typeOk =
//       typeFilter === 'All' ||
//       (typeFilter === 'CAF' && d.source === 'CAF') ||
//       (normalizeStr(d.type) === normalizeStr(typeFilter))

//     const commOk =
//       (communityFilter === 'All') ||
//       matchesLocation(d, communityFilter)

//     const archOk = showArchived || !d.archivedAt

//     return matchQ && typeOk && commOk && archOk
//   })
// }, [docs, cafRows, q, typeFilter, communityFilter, includeCAF, showArchived, nameForCode, sessionCommunities])


// limit dropdown = “All” + just the communities the user is a member of, while the results still show all communities when “All” is selected.
const rows = useMemo(() => {
  const s = normalizeStr(q)
  const all = [
    ...(Array.isArray(docs) ? docs.map(d => ({ ...d, source: 'Community' })) : []),
    ...(includeCAF ? (cafRows || []) : [])
  ]

  return all.filter(d => {
    const matchQ =
      !s ||
      normalizeStr(d.title).includes(s) ||
      (Array.isArray(d.tags) && normalizeStr(d.tags.join(' ')).includes(s)) ||
      normalizeStr(codeOf(d)).includes(s) ||
      normalizeStr(audienceLabel(d,nameForCode)).includes(s)

    const typeOk =
      typeFilter === 'All' ||
      (typeFilter === 'CAF' && d.source === 'CAF') ||
      (normalizeStr(d.type) === normalizeStr(typeFilter))

    const commOk =
      (communityFilter === 'All') ||
      matchesLocation(d, communityFilter)

    const archOk = showArchived || !d.archivedAt

    return matchQ && typeOk && commOk && archOk
  })
}, [docs, cafRows, q, typeFilter, communityFilter, includeCAF, showArchived, nameForCode])

  /* selection */
  const allIds = rows.map((r) => r.id)
  const allChecked = allIds.length > 0 && allIds.every((id) => selected.has(id))
  function toggleAll() {
    setSelected((prev) => {
      if (allChecked) return new Set()
      const next = new Set(prev)
      allIds.forEach((id) => next.add(id))
      return next
    })
  }
  function toggleRow(id) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }
  const selectedDocsForZip = useMemo(
    () => rows.filter((r) => selected.has(r.id) && hasDownload(r)),
    [rows, selected]
  )

  /* actions */
  async function onUpload() {
    if (!uTitle.trim() || !uFile || (uAudience==='community'&&!uCommunity) || (uAudience==='selected'&&!uLocations.length)) return
    if(uAudience!=='community'&&!isAdminOrHigher)return showError('Administrator required','Shared documents must be uploaded by an administrator.')
    if (uFile.size > 50 * 1024 * 1024) {
      // 50MB
      return showError(
        'File too large',
        'Please upload a file 50MB or smaller.'
      )
    }
    setUploading(true)
    try {
      // Build BOTH code and display name for rules compatibility
      const audience = audienceFor(uAudience,uAudience==='community'?[uCommunity]:uLocations,communities)
      const {communityCode,communityName} = audience // // exact display name (users[].communities)

      const fileMeta = await uploadToStorage(uAudience==='community'?communityCode:'shared', uFile)
      const tagsArray = uTags.split(',').map((s) => s.trim()).filter(Boolean)
      const sess = session || {}

      // optimistic add so UI feels responsive
      const optimistic = {
        id: `local-${Date.now()}`,
        title: uTitle.trim(),
        type: uType && uType !== '—' ? uType : '',
        ...audience,
        communityCode,
        communityName,
        community: communityName,
        file: fileMeta,
        archivedAt: null,
        createdAt: new Date().toISOString(),
        source: 'Community',
        tags: tagsArray,
        notes: uNotes || '',
        createdBy: {
          email: String(sess.email || ''),
          displayName: String(sess.displayName || ''),
        },
      }
      setDocs((prev) => [optimistic, ...(prev || [])])

      // Persist to Firestore
      await addDocFS({
        title: optimistic.title,
        type: optimistic.type,
        ...audience,
        communityCode,
        communityName,
        community: communityName,
        file: fileMeta,
        archivedAt: null,
        tags: tagsArray,
        notes: optimistic.notes,
        createdBy: optimistic.createdBy,
      })
      setSelected(new Set())

      // clear inputs (preserve auto-select if single community)
      setUTitle('')
      setUType('—')
      setUTags('')
      setUNotes('')
      setUFile(null)
      setULocations([])
      setUAudience('community')
      if (scopedCommunities.length === 1) {
        const only = scopedCommunities[0]
        setUCommunity(only.code || only.communityCode)
      } else {
        setUCommunity('')
      }
    } catch (e) {
      console.error('upload failed', e)
      const email = session?.email || 'unknown'
      const cc = uCommunity || communityFilter || 'n/a'
      showError(
        'Upload failed',
        `${e?.message || 'Missing or insufficient permissions.'}\n\n` +
          `Signed-in: ${email}\nCommunity: ${cc}`
      )
      setDocs((prev) => prev.filter((r) => !String(r.id).startsWith('local-')))
    } finally {
      setUploading(false)
    }
  }

  /* UI */
    return (
    <div>
      {/* local override to kill ellipsis in Actions column */}
      <style>{`
        .table.docs-table .docs-actions-head,
        .table.docs-table .docs-actions-cell {
          overflow: visible !important;
          text-overflow: clip !important;
          white-space: nowrap !important;
          padding-left: 12px;
          padding-right: 28px;
        }

        .docs-action-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 28px;
          height: 28px;
          padding: 0;
          margin: 0;
          border: 0;
          background: transparent;
          color: var(--color-primary);
          cursor: pointer;
        }

        .docs-action-btn:hover {
          color: var(--color-secondary);
          background: transparent;
          text-decoration: none;
        }

        .docs-action-btn.restore {
          color: var(--color-ok);
        }

        .docs-title-head,
        .docs-from-head,
        .docs-from-cell {
          overflow: visible !important;
          text-overflow: clip !important;
          white-space: nowrap !important;
        }

        .docs-title-cell,
        .docs-title-cell a {
          overflow: visible !important;
          text-overflow: clip !important;
          white-space: normal !important;
          word-break: break-word;
          overflow-wrap: anywhere;
        }

        /* give table rows a little more breathing room */
        .table.docs-table tbody tr td {
          padding-top: 10px;
          padding-bottom: 10px;
        }
      `}</style>

      <h2
  style={{
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginTop: 14,
    marginBottom: 12,
  }}
>
  <span>Community Documents</span>
  <span style={{ flex: 1 }} />
  <ExportDocsZip
    items={selectedDocsForZip}
    onDone={() => setSelected(new Set())}
  />
</h2>

      {/* Filters row (inline) */}
      <div
        className="mobile-stack-grid card"
        style={{
          display: 'grid',
          gridTemplateColumns: '1.2fr 0.8fr 0.8fr auto auto auto',
          gap: 12,
          alignItems: 'end',
        }}
      >
        <div>
          <label style={{ marginBottom: 6 }}>Search</label>
          <input
            placeholder="Title, type, tags, community…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>

        <div>
          <label style={{ marginBottom: 6 }}>Type</label>
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
            {docTypeOptions.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label style={{ marginBottom: 6 }}>Community</label>
          <select
            value={communityFilter}
            onChange={(e) => setCommunityFilter(e.target.value)}
          >
            {communityOptions.map((c) => (
              <option key={c.code} value={c.code}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        <label
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            paddingBottom: 2,
          }}
        >
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
          />
          Show archived
        </label>

        <label
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            paddingBottom: 2,
          }}
        >
          <input
            type="checkbox"
            checked={includeCAF}
            onChange={(e) => setIncludeCAF(e.target.checked)}
          />
          Include CAF uploads
        </label>

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            onClick={async () => {
              try {
                const rows = await listDocsFS(null, !!showArchived)
                setDocs(Array.isArray(rows) ? rows : [])
              } catch (e) {
                showError('Refresh failed', e?.message || 'Unable to reload documents.')
              }
            }}
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Upload block — two rows */}

      <div className="card" style={{ marginTop: 12 }}>
        {isAdminOrHigher&&<div style={{marginBottom:20}}><label>Applies to</label><div style={{display:'flex',gap:20,flexWrap:'wrap'}}>{[['community','One location'],['selected','Selected locations'],['all','All locations']].map(([value,label])=><label key={value} style={{display:'flex',alignItems:'center',gap:6}}><input type="radio" name="documentAudience" style={{width:'auto'}} checked={uAudience===value} onChange={()=>{setAudienceBefore({scope:uAudience,locations:[...uLocations]});setUAudience(value);setAudienceModal(value==='selected')}}/>{label}</label>)}</div>{uAudience==='selected'&&<button type="button" onClick={()=>{setAudienceBefore({scope:uAudience,locations:[...uLocations]});setAudienceModal(true)}} style={{marginTop:10}}>{`${uLocations.length} locations selected · Change`}</button>}</div>}
        <SharedUploadDialog open={audienceModal} scope={uAudience} busy={false} onClose={()=>{setUAudience(audienceBefore.scope);setULocations(audienceBefore.locations);setAudienceModal(false)}} selectionOnly>
          {audienceModal&&<div>{uAudience==='selected'&&<div style={{marginTop:12}}><div className="shared-location-grid">{scopedCommunities.map(c=>{const code=c.code||c.communityCode;return <label key={code} style={{display:'flex',alignItems:'center',gap:8}}><input type="checkbox" style={{width:'auto'}} checked={uLocations.includes(code)} onChange={e=>setULocations(old=>e.target.checked?[...old,code]:old.filter(x=>x!==code))}/>{c.communityName||c.name||code}</label>})}</div><small>{uLocations.length} locations selected · One shared file</small></div>}<div style={{display:'flex',justifyContent:'flex-end',marginTop:20}}><button type="button" className="primary" disabled={uAudience==='selected'&&!uLocations.length} onClick={()=>setAudienceModal(false)}>{uAudience==='all'?'Use all locations':'Use selected locations'}</button></div></div>}
        </SharedUploadDialog>
        {/* Row 1: Title, Type, Community, File */}
        <div className="mobile-stack-grid shared-upload-fields"
          style={{
            display: 'grid',
            gridTemplateColumns: '1.1fr 0.55fr 0.75fr 1.6fr',
            gap: 12,
            alignItems: 'end',
          }}
        >
          <div>
            <label>Title</label>
            <input
              placeholder="e.g., Roof Warranty"
              value={uTitle}
              onChange={(e) => setUTitle(e.target.value)}
            />
          </div>

          <div>
            <label>Type</label>
            <select value={uType} onChange={(e) => setUType(e.target.value)}>
              <option value="—">—</option>
              {docTypes.map((t) => (
                <option key={t.id} value={t.name}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label>{uAudience==='community'?'Community':'Coverage'}</label>
            {uAudience!=='community'?<input readOnly value={uAudience==='all'?'All locations':`${uLocations.length} selected locations`}/>:<select value={uCommunity} onChange={(e) => setUCommunity(e.target.value)}>
              {scopedCommunities.length === 1 && !uCommunity && (
                <option value="">—</option>
              )}
              {scopedCommunities.map((c) => {
                const code = c.code || c.communityCode
                const name = c.communityName || c.name || c.code
                return (
                  <option key={code} value={code}>
                    {name}
                  </option>
                )
              })}
              {scopedCommunities.length > 1 && <option value="">— Select —</option>}
            </select>}
          </div>

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-end',
            }}
          >
            <label style={{ marginBottom: 0 }}>File</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <input
                id="upload-file-input"
                type="file"
                style={{ display: 'none' }}
                onChange={(e) => setUFile(e.target.files?.[0] || null)}
              />
              <label
                htmlFor="upload-file-input"
                className="btn"
                style={{
                  cursor: 'pointer',
                  userSelect: 'none',
                  height: 40,
                  display: 'inline-flex',
                  alignItems: 'center',
                  paddingInline: 12,
                  position: 'relative',
                  top: '6px',
                }}
              >
                Choose File
              </label>
              <span
                className="muted"
                style={{
                  height: 40,
                  display: 'inline-flex',
                  alignItems: 'center',
                  color: 'var(--color-text)',
                  fontWeight: 600,
                  fontSize: 12,
                  opacity: 0.75,
                  position: 'relative',
                  top: '6px',
                  userSelect: 'none',
                }}
              >
                {uFile ? uFile.name : 'No file chosen'}
              </span>
            </div>
          </div>
        </div>

        {/* Row 2: Tags, Notes, Upload button (right) */}
        <div className="mobile-stack-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1.4fr auto',
            gap: 12,
            marginTop: 12,
            alignItems: 'end',
          }}
        >
          <div>
            <label>Tags</label>
            <input
              placeholder="comma separated (e.g., roof, warranty)"
              value={uTags}
              onChange={(e) => setUTags(e.target.value)}
            />
          </div>

          <div>
            <label>Notes</label>
            <input
              placeholder="optional notes"
              value={uNotes}
              onChange={(e) => setUNotes(e.target.value)}
            />
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button
              className="primary"
              onClick={onUpload}
              disabled={uploading || !uTitle.trim() || !uFile || (uAudience==='community'&&!uCommunity) || (uAudience==='selected'&&!uLocations.length)}
            >
              {uploading ? 'Uploading…' : 'Upload'}
            </button>
          </div>
        </div>
      </div>

      {distributionNotice&&<p role="status">{distributionNotice}</p>}
      {distributionDoc&&<DocumentDistributionDrawer key={distributionDoc.id} document={distributionDoc} communities={communities} onClose={()=>setDistributionDoc(null)} onSaved={async()=>{const fresh=await listDocsFS(null,!!showArchived);setDocs(fresh);setDistributionDoc(null);setDistributionNotice('Document distribution saved.')}}/>}
      {/* Results */}
      <div className="card" style={{ marginTop: 12 }}>
        <div className="responsive-table-scroll" tabIndex={0} role="region" aria-label="Scrollable table"><table className="table docs-table" style={{ width: '100%' }}>
          <colgroup>
            <col style={{ width: '4%' }} />
            <col style={{ width: '34%' }} />
            <col style={{ width: '11%' }} />
            <col style={{ width: '17%' }} />
            <col style={{ width: '16%' }} />
            <col style={{ width: '13%' }} />
            <col style={{ width: 148 }} />
          </colgroup>
          <thead>
            <tr>
              <th style={{ width: 44 }}>
                <input type="checkbox" checked={allChecked} onChange={toggleAll} />
              </th>
              <th className="docs-title-head nowrap">Title</th>
              <th style={{ minWidth: 160 }}>Type</th>
              <th className="docs-from-head">From</th>
              <th className="nowrap">Community</th>
              <th style={{ minWidth: 180 }}>Created</th>
              <th
                className="docs-actions-head"
                style={{ textAlign: 'center', minWidth: 120 }}
              >
                Actions
              </th>
            </tr>
          </thead>

          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={7}>
                  <div className="muted">
                    {busy ? 'Loading…' : 'No matching documents.'}
                  </div>
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <tr key={r.id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selected.has(r.id)}
                      onChange={() => toggleRow(r.id)}
                    />
                  </td>

                  <td className="docs-title-cell nowrap">
                    {hasDownload(r) ? (
                      <a
                        href={r.file.downloadURL}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {r.title || '—'}
                      </a>
                    ) : (
                      r.title || '—'
                    )}
                  </td>

                  {/* Type column – now respects CAF docType & proposals */}
                  <td>{displayType(r)}</td>

                  <td className="docs-from-cell">
                    {r.source === 'CAF'
                      ? r.cafNumber || '—'
                      : 'Community'}
                  </td>

                  <td style={{maxWidth:280,whiteSpace:'normal'}}>{audienceLabel(r,nameForCode)}</td>
                  <td>{fmtTS(r.createdAt)}</td>

                  <td className="docs-actions-cell" style={{ textAlign: 'center' }}>
                    <div className="docs-distribution-action">
                    {isAdminOrHigher&&r.source!=='CAF'&&!r.archivedAt&&<button type="button" className="docs-action-btn" data-tooltip="Edit distribution" aria-label="Edit distribution" onClick={()=>{setDistributionNotice('');setDistributionDoc(r)}}><ActionIcon kind="distribution"/></button>}
                    {r.source === 'CAF' ? (
                      <span className="muted">—</span>
                    ) : r.archivedAt ? (
                      <button
                        className="docs-action-btn restore"
                        data-tooltip="Restore"
                        aria-label="Restore document"
                        onClick={() =>
                          restoreDocFS(r.id)
                            .then(async () => {
                              try {
                                const rows = await listDocsFS(null, !!showArchived)
                                setDocs(Array.isArray(rows) ? rows : [])
                              } catch (e) {
                                showError(
                                  'Restore failed',
                                  e?.message || 'Unable to refresh.'
                                )
                              }
                            })
                            .catch((err) =>
                              showError(
                                'Restore failed',
                                err?.message || 'Request was rejected.'
                              )
                            )
                        }
                      >
                        <ActionIcon kind="restore" />
                      </button>
                    ) : (
                      <button
                        className="docs-action-btn"
                        data-tooltip="Archive"
                        aria-label="Archive document"
                        onClick={() => {
                          if (adminOnlyArchive && !isAdminOrHigher) {
                            openRestrictedArchiveDialog(r)
                            return
                          }
                          softDeleteDocFS(r.id)
                            .then(async () => {
                              try {
                                const rows = await listDocsFS(null, !!showArchived)
                                setDocs(Array.isArray(rows) ? rows : [])
                              } catch (e) {
                                showError(
                                  'Archive failed',
                                  e?.message || 'Unable to refresh.'
                                )
                              }
                            })
                            .catch((err) =>
                              showError(
                                'Archive failed',
                                err?.message || 'Request was rejected.'
                              )
                            )
                        }}
                      >
                        <ActionIcon kind="archive" />
                      </button>
                    )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table></div>
      </div>

      {/* Custom dialog */}
      <ConfirmDialog
        open={dlg.open}
        title={dlg.title}
        message={dlg.message}
        confirmLabel={dlg.confirmLabel}
        cancelLabel={dlg.cancelLabel}
        tone={dlg.tone}
        hideCancel={dlg.hideCancel}
        onConfirm={dlg.onConfirm || closeDlg}
        onCancel={dlg.onCancel || closeDlg}
      />
    </div>
  )
}
